import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import type { ExclusionFields } from "./exclusion-expression";
import { RULE_CATEGORY_META, type DetectorMode } from "./policy-data";
import { getCategoryForFinding, getRuleTitleFallback } from "./risk-utils";

// The ready-made rules offered for a finding-originated exclusion. Each option
// carries a complete rule in `fields`, which goes straight to the mutation,
// skipping the expression serialize -> parse round trip. Two options have no
// fields and so cannot be saved as-is: "custom", which reveals the DSL textarea
// instead, and an "exact" whose plaintext is still behind an unfired reveal.
export interface ExclusionOption {
  value: "exact" | "rule" | "source" | "custom";
  title: string;
  hint: string;
  fields?: ExclusionFields;
}

/** The value an exact-match rule would be written against. The masked list
 * pages hold only `label` (the redacted placeholder) until the operator
 * selects the option and the audited reveal resolves into `value`. */
export interface ExactCandidate {
  value?: string;
  label: string;
}

// Where the plaintext for an exact rule comes from, in order: the finding
// itself (the chat surfaces carry it), a resolved reveal, or — on the masked
// list pages — nothing yet, in which case the redacted placeholder labels an
// option that exists only to be selected. `undefined` means no exact rule is
// on offer at all: a multi-row selection, or `canReveal` false because the
// caller lacks `chat:read` or the finding has no match behind it (a judge
// verdict), in which case the option must not appear as a dead affordance.
export function exactCandidate(
  single: RiskResult | undefined,
  revealed: string | null,
  canReveal: boolean,
): ExactCandidate | undefined {
  if (!single) return undefined;
  if (single.match) return { value: single.match, label: single.match };
  if (revealed) return { value: revealed, label: revealed };
  if (canReveal && single.matchRedacted) return { label: single.matchRedacted };
  return undefined;
}

const CUSTOM_OPTION: ExclusionOption = {
  value: "custom",
  title: "Write it myself",
  hint: "Regex, entity types, and rule/source filters.",
};

// Entity-type exclusions are a Presidio concept; the LLM analyzer does not
// apply them, so the DSL box is not advertised as offering them.
const LLM_CUSTOM_OPTION: ExclusionOption = {
  value: "custom",
  title: "Write it myself",
  hint: "Regex and rule/source filters.",
};

// How the source option names a detector. The option's value stays the raw
// source (that is what the rule is written against), but scanner and engine
// names are implementation detail the policy author never sees elsewhere, so
// the title says what the detector looks for instead. Sources without an
// entry fall back to their category's label, then to the source itself.
const SOURCE_TITLE: Record<string, string> = {
  gitleaks: "secret scanning",
  presidio: "personal data detection",
  prompt_injection: "prompt injection detection",
  llm_judge: "prompt policies",
  llm_analyzer: "the risk model",
};

function sourceTitle(source: string): string {
  const named = SOURCE_TITLE[source];
  if (named) return named;
  const category = getCategoryForFinding(source);
  return category ? RULE_CATEGORY_META[category].label : source;
}

function fields(
  matchType: ExclusionFields["matchType"],
  matchValue: string,
): ExclusionFields {
  return { matchType, matchValue, ruleIdFilter: "", sourceFilter: "" };
}

function shared<K extends "ruleId" | "source">(
  results: RiskResult[],
  key: K,
): string | undefined {
  const value = results[0]?.[key];
  return value && results.every((r) => r[key] === value) ? value : undefined;
}

// Builds the options valid for a selection. An exact-value rule needs a single
// finding — its plaintext comes from the finding itself on the chat surfaces,
// or from `exact` on the masked list pages, where the raw match never reaches
// the browser until it is unmasked. Rule and source rules need every selected
// row to agree on that field — except `presetRuleId`, which an opener that
// already knows the rule (the Watchdog signal drawer) supplies directly, so the
// rule option doesn't depend on findings having loaded. The selection wins when
// it determines a rule of its own.
export function exclusionOptions(
  results: RiskResult[],
  exact?: ExactCandidate,
  presetRuleId?: string,
  mode: DetectorMode = "presidio",
): ExclusionOption[] {
  const options: ExclusionOption[] = [];

  const own = results.length === 1 ? results[0]?.match : undefined;
  const match = exact?.value ?? own;
  const label = exact?.label ?? own;
  if (label) {
    options.push({
      value: "exact",
      title: `Just this value — ${label}`,
      hint: match
        ? "Only this exact value stops being flagged."
        : "Select to reveal the value — this is recorded in the audit log.",
      // Absent while a reveal is still pending: the option renders and can be
      // selected (that is what fires the reveal), but cannot yet be saved.
      fields: match ? fields("exact", match) : undefined,
    });
  }

  const ruleId = shared(results, "ruleId") ?? presetRuleId;
  if (ruleId) {
    options.push({
      value: "rule",
      title: `Any ${getRuleTitleFallback(ruleId)} finding`,
      hint: "Every finding from this detection rule stops being flagged.",
      fields: fields("rule_id", ruleId),
    });
  }

  const source = shared(results, "source");
  if (source) {
    options.push({
      value: "source",
      title: `Anything detected by ${sourceTitle(source)}`,
      hint: "Every finding from this detector stops being flagged.",
      fields: fields("source", source),
    });
  }

  options.push(mode === "llm" ? LLM_CUSTOM_OPTION : CUSTOM_OPTION);
  return options;
}
